import React, { useMemo, useState } from 'react';
import { Modal, Button, Select, TreeSelect, message } from 'antd';
import { useMutation, useQuery } from '@apollo/client';
import {
  SpaceRelation,
  ASSIGN_USER_TO_SPACE,
  AssignUserToSpaceVariables,
  AssignUserToSpaceResponse,
  GET_USER_SPACE_ASSIGNMENTS,
  GetUserSpaceAssignmentsResponse,
  GetUserSpaceAssignmentsVariables,
  AUTH_API_URL,
} from './SpaceAssignments';
import { UPDATE_USER, UpdateUserMutationResponse, UpdateUserMutationVariables } from './Users';
import { GET_SITE_HIERARCHY } from '@/hooks/useSiteOptions';
import { SiteOption } from '@/hooks/useSiteHierarchyFilters';
import {
  flattenHierarchyToSpaceOptions,
  buildTreeDataWithSelectAll,
  getDefaultExpandedKeys,
  getSpaceIdsToAssign,
} from './SpaceTreeUtils';
import { formatOperationError } from '@/api/formatError';
import { runDowngradeWithSpaces } from './downgradeOrchestrator';

const { Option } = Select;

// Backend roles accepted by `updateUser`. The downgrade flow only ever
// targets `butlr_user`, but the prop is typed as the union so callers can't
// silently pass something the rank check would reject (e.g. `superuser`).
export type AuthApiRole = 'owner' | 'admin' | 'butlr_user';

interface AssignSpaceAfterRoleChangeModalProps {
  open: boolean;
  userId: string;
  // Roles to commit on Confirm. The role change is deferred to the modal so
  // Cancel is a true abort — no role downgrade lands without space coverage.
  rolesToAssign: AuthApiRole[];
  // Roles the user currently has, used to revert the role mutation if every
  // space assignment fails. Without this, an all-fail outcome would leave
  // the user as a Member with zero spaces and no automatic recovery.
  originalRoles: AuthApiRole[];
  onCancel: () => void;
  onSuccess: () => void;
}

export const AssignSpaceAfterRoleChangeModal: React.FC<AssignSpaceAfterRoleChangeModalProps> = ({
  open,
  userId,
  rolesToAssign,
  originalRoles,
  onCancel,
  onSuccess,
}) => {
  const [selectedSpaces, setSelectedSpaces] = useState<string[]>([]);
  const [relation, setRelation] = useState<SpaceRelation>('can_view');
  // Single submit lock for the whole orchestrator run. Apollo's per-mutation
  // `loading` flags are scoped to one call each, so combining them as the
  // modal's "busy" signal leaves microscopic gaps between steps (role
  // mutation resolved → parallel assigns dispatched → assigns resolved →
  // optional rollback). A Cancel/X/overlay/Esc click in any of those gaps
  // could escape into the abort path after the role had already committed.
  // `isSubmitting` is set true around the entire `runDowngradeWithSpaces`
  // call and unset in a finally block so `isBusy` stays true continuously.
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: hierarchyData, loading: hierarchyLoading } = useQuery(GET_SITE_HIERARCHY, {
    skip: !open,
  });

  // Existing direct space grants for this user. Surfaced as a small note so
  // the admin can see what the user already has before adding more.
  const { data: existingData } = useQuery<
    GetUserSpaceAssignmentsResponse,
    GetUserSpaceAssignmentsVariables
  >(GET_USER_SPACE_ASSIGNMENTS, {
    variables: { user_id: userId },
    skip: !open || !userId,
    context: { uri: AUTH_API_URL },
    fetchPolicy: 'cache-and-network',
  });
  const existingCount = existingData?.userSpaceAssignments?.length ?? 0;

  const availableSpaces = useMemo(() => {
    const sites = (hierarchyData?.sites?.data || []) as SiteOption[];
    return flattenHierarchyToSpaceOptions(sites);
  }, [hierarchyData]);

  const treeData = useMemo(() => buildTreeDataWithSelectAll(availableSpaces), [availableSpaces]);

  const defaultExpandedKeys = useMemo(
    () => getDefaultExpandedKeys(availableSpaces),
    [availableSpaces],
  );

  // Per-mutation `loading` flags are intentionally NOT destructured here.
  // `isSubmitting` (above) is the single source of truth for the modal's
  // busy state — covering all five orchestrator outcomes plus any future
  // multi-step flows. Any new mutation added to this modal MUST be driven
  // through `setIsSubmitting(true/false)` (typically inside a try/finally
  // around the awaited call) so the close/disable guards stay correct.
  const [assignToSpace] = useMutation<AssignUserToSpaceResponse, AssignUserToSpaceVariables>(
    ASSIGN_USER_TO_SPACE,
    { context: { uri: AUTH_API_URL } },
  );

  const [updateUser] = useMutation<UpdateUserMutationResponse, UpdateUserMutationVariables>(
    UPDATE_USER,
    { context: { uri: AUTH_API_URL } },
  );

  const spacesToAssign = useMemo(
    () => getSpaceIdsToAssign(selectedSpaces, availableSpaces),
    [selectedSpaces, availableSpaces],
  );
  const canConfirm = spacesToAssign.length > 0 && !hierarchyLoading;

  const handleConfirm = async () => {
    // Defensive: button is disabled, but guard so a fast-clicker can't bypass.
    if (spacesToAssign.length === 0) {
      message.error('Please select at least one space');
      return;
    }

    setIsSubmitting(true);
    try {
      // Logic lives in `runDowngradeWithSpaces` so the rollback path is
      // unit-tested without driving Antd's TreeSelect from RTL. The five
      // outcomes correspond 1:1 to the callbacks below.
      await runDowngradeWithSpaces({
        userId,
        rolesToAssign,
        originalRoles,
        spacesToAssign,
        relation,
        updateUser: ({ id, roles }) => updateUser({ variables: { id, input: { roles } } }),
        assignToSpace: (input) => assignToSpace({ variables: { input } }),
        // The four "modal closes via parent" callbacks (onFullSuccess,
        // onPartialSuccess, onAllSpacesFailedRoleReverted,
        // onAllSpacesFailedRevertAlsoFailed) intentionally do NOT reset
        // local state. Each calls onSuccess()/onCancel() which clears
        // pendingRoleDowngrade in the parent, which conditionally
        // unmounts this modal via `{pendingRoleDowngrade && ...}`. The
        // local resets would be dead writes — the next mount gets fresh
        // useState initial values. Only handleCancel below resets
        // explicitly, since it's also the path used when the user
        // cancels mid-edit before any mutation has fired.
        callbacks: {
          onRoleUpdateFailed: (err) => {
            console.error('Failed to update user role during downgrade:', err);
            message.error(formatOperationError('update user role', err));
          },
          onFullSuccess: () => {
            message.success('Role updated and spaces assigned');
            onSuccess();
          },
          onPartialSuccess: (failed, total) => {
            message.warning(`Role updated. ${total - failed} space(s) assigned, ${failed} failed.`);
            onSuccess();
          },
          onAllSpacesFailedRoleReverted: () => {
            message.error('Could not assign any spaces; role change reverted.');
            onCancel();
          },
          onAllSpacesFailedRevertAlsoFailed: (revertErr) => {
            console.error('Failed to revert role after assignment failure:', revertErr);
            message.error(
              'Failed to assign spaces and failed to revert role. Reassign manually or contact support.',
            );
            onSuccess();
          },
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    // Cancel is only reachable in the pre-commit window — the busy guards
    // (closable/maskClosable/keyboard + disabled button) block dismissal
    // while step 1 / step 2 / rollback are in flight, and every post-step
    // branch in handleConfirm calls onCancel or onSuccess itself. So
    // Cancel here is always a true abort: the role has not been committed.
    setSelectedSpaces([]);
    setRelation('can_view');
    onCancel();
  };

  const isBusy = isSubmitting;

  return (
    <Modal
      title="Assign spaces before downgrade"
      open={open}
      onCancel={handleCancel}
      // Block dismissal via X, overlay click, and Esc while a mutation is in
      // flight, so the admin can't close mid-`updateUser` and leave the role
      // mutation racing with no UI to recover. (`Cancel` is also disabled
      // via its own `disabled={isBusy}` below.)
      closable={!isBusy}
      maskClosable={!isBusy}
      keyboard={!isBusy}
      footer={[
        <Button key="cancel" onClick={handleCancel} disabled={isBusy}>
          Cancel
        </Button>,
        <Button
          key="confirm"
          type="primary"
          onClick={handleConfirm}
          loading={isBusy}
          disabled={!canConfirm}
          className="!bg-btn-action-bg !text-btn-action-text hover:!bg-btn-action-hover !border-btn-action-bg"
        >
          Apply downgrade
        </Button>,
      ]}
      width={480}
    >
      <p className="text-content-secondary text-sm mb-4">
        Changing this user&apos;s role to Member requires explicit space assignments — without them
        the user will have no access. Select at least one space to continue, or cancel to abort the
        role change.
      </p>
      {existingCount > 0 && (
        <p className="text-content-secondary text-xs mb-3">
          User already has direct access to {existingCount} space{existingCount === 1 ? '' : 's'};
          additions below are added on top.
        </p>
      )}
      <div className="bg-surface-sunken rounded-lg p-4">
        <div className="flex items-center gap-2">
          <TreeSelect
            treeData={treeData}
            value={selectedSpaces}
            onChange={(value) =>
              setSelectedSpaces(Array.isArray(value) ? value : value ? [value] : [])
            }
            placeholder={hierarchyLoading ? 'Loading spaces...' : 'Select a space'}
            treeCheckable
            showCheckedStrategy={TreeSelect.SHOW_PARENT}
            treeDefaultExpandedKeys={defaultExpandedKeys}
            className="flex-1"
            loading={hierarchyLoading}
            disabled={hierarchyLoading || isBusy}
            maxTagCount={3}
            maxTagPlaceholder={(omittedValues) => `+${omittedValues.length} more`}
            dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
            treeIcon
          />
          <Select
            value={relation}
            onChange={(value) => setRelation(value)}
            style={{ width: 120 }}
            disabled={isBusy}
          >
            <Option value="can_view">Can View</Option>
            <Option value="can_edit">Can Edit</Option>
          </Select>
        </div>
      </div>
    </Modal>
  );
};

export default AssignSpaceAfterRoleChangeModal;
